import {useMutation, useQuery, useQueryClient} from "@tanstack/react-query";
import {message} from "antd";
import {AxiosError, AxiosResponse} from "axios";
import {TransactionChargeSettings, ChargeBearer} from "../domain/types";
import {createChargeSettingsRepository} from "../data/repositories";
import {ChargeBearer as ChargeBearerEnum} from "../../../components/payouts/v2/tabs/transaction-settings-tab";

// Query key for cache management
export const CHARGE_SETTINGS_QUERY_KEY = ["transaction_charge_settings"] as const;

/**
 * Hook for fetching transaction charge settings.
 * Returns domain TransactionChargeSettings type.
 */
export const useTransactionChargeSettings = () => {
  const repository = createChargeSettingsRepository();

  return useQuery<TransactionChargeSettings | null, AxiosError<{message: string}>>({
    queryKey: CHARGE_SETTINGS_QUERY_KEY,
    queryFn: () => repository.getSettings(),
    staleTime: Infinity,
  });
};

/**
 * Hook for creating/updating transaction charge settings.
 * Invalidates the query cache on success.
 */
export const useUpsertTransactionChargeSettings = () => {
  const queryClient = useQueryClient();
  const repository = createChargeSettingsRepository();

  return useMutation<
    TransactionChargeSettings,
    AxiosError<{message: string}>,
    ChargeBearer
  >({
    mutationFn: (chargeBearer) => repository.upsertSettings(chargeBearer),
    onError: (error) => {
      message.error(error.response?.data?.message || error.message);
    },
    onSettled: () => {
      queryClient.invalidateQueries({queryKey: CHARGE_SETTINGS_QUERY_KEY});
    },
  });
};

// ==================== Legacy-Compatible Hooks ====================

/**
 * Legacy data type for backwards compatibility.
 * Matches TransactionChargeSettingsValidApiResponse from payout.ts.
 */
export interface LegacyChargeSettingsData {
  id: number;
  charge_bearer: ChargeBearerEnum;
  createdAt: string;
  updatedAt: string;
  user_id: number;
}

/**
 * Legacy response type for backwards compatibility.
 * Matches the old AxiosResponse wrapper format.
 */
export type LegacyChargeSettingsResponse = AxiosResponse<LegacyChargeSettingsData | null>;

/**
 * Legacy-compatible hook for fetching charge settings.
 * Returns data in the old API response format (AxiosResponse shape).
 *
 * @deprecated Use useTransactionChargeSettings for new code.
 */
export const useFetchTransactionChargeSettings = () => {
  const repository = createChargeSettingsRepository();

  return useQuery<
    LegacyChargeSettingsResponse,
    AxiosError<{status: boolean; message: string}>
  >({
    queryKey: CHARGE_SETTINGS_QUERY_KEY,
    queryFn: async () => {
      const settings = await repository.getSettings();

      // Return AxiosResponse-compatible structure
      const response: LegacyChargeSettingsResponse = {
        data: settings
          ? {
              id: settings.id,
              charge_bearer: settings.chargeBearer as ChargeBearerEnum,
              createdAt: settings.createdAt,
              updatedAt: settings.updatedAt,
              user_id: 0, // Not available from domain, but required for type compatibility
            }
          : null,
        status: 200,
        statusText: "OK",
        headers: {},
        config: {} as LegacyChargeSettingsResponse["config"],
      };

      return response;
    },
    staleTime: Infinity,
  });
};

/**
 * Legacy-compatible hook for updating charge settings.
 * Accepts payload in old snake_case format.
 *
 * @deprecated Use useUpsertTransactionChargeSettings for new code.
 */
export const useUpdateTransactionChargeSettings = () => {
  const queryClient = useQueryClient();
  const repository = createChargeSettingsRepository();

  return useMutation<
    unknown,
    AxiosError<{message: string}>,
    {charge_bearer: "coach" | "customer"}
  >({
    mutationFn: (payload) => repository.upsertSettings(payload.charge_bearer),
    onError: (error) => {
      message.error(error.response?.data?.message || error.message);
    },
    onSettled: () => {
      queryClient.invalidateQueries({queryKey: CHARGE_SETTINGS_QUERY_KEY});
    },
  });
};
